import { useCallback, useEffect, useRef, useState } from 'react';
import { Check, Copy } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Shortens a prefixed support ID, eliding the MIDDLE rather than the tail.
 *
 * Support IDs are prefixed UUIDs of differing lengths — `stk_`/`ssg_`/`sbr_`
 * carry 20 hex chars, `rpt_`/`fair_` carry 32 — so a fixed character count
 * would cut some of them mid-prefix. Splitting on the prefix keeps the record
 * type readable in every case.
 *
 * The ellipsis sits in the middle because every id of a given kind shares its
 * prefix: dropping the tail leaves rows that differ only in a few characters
 * at the same offset, which read as identical at a glance. Keeping both ends
 * gives each row two independent places to differ.
 */
export function shortenSupportId(id: string, headLength = 6, tailLength = 6): string {
  const elide = (s: string) =>
    s.length > headLength + tailLength + 1
      ? `${s.slice(0, headLength)}…${s.slice(-tailLength)}`
      : s;

  const sep = id.indexOf('_');
  if (sep === -1) return elide(id);
  const prefix = id.slice(0, sep + 1);
  return `${prefix}${elide(id.slice(sep + 1))}`;
}

/**
 * Copyable reference ID for a support interaction — ticket, suggestion, bug
 * report, builder report or Fias AI report.
 *
 * Admins cross-reference these against the roadmap todo lists the sweep routes
 * submissions into, so the ID has to be readable in-place; clicking copies the
 * full value rather than the abbreviated one on screen.
 */
export function SupportRefId({
  id,
  label = 'ID',
  variant = 'short',
  showLabel = false,
  className,
}: {
  id: string;
  /** Describes the record type in the tooltip / screen-reader label. */
  label?: string;
  /** `short` abbreviates for table cells; `full` prints the whole ID. */
  variant?: 'short' | 'full';
  /** Print `label` alongside the ID — for detail headers, where there is room. */
  showLabel?: boolean;
  className?: string;
}) {
  const [copied, setCopied] = useState(false);
  const resetTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (resetTimer.current) clearTimeout(resetTimer.current);
    };
  }, []);

  const handleCopy = useCallback(
    async (e: React.MouseEvent) => {
      // These chips live inside click-to-open table rows.
      e.stopPropagation();
      try {
        await navigator.clipboard?.writeText(id);
        setCopied(true);
        if (resetTimer.current) clearTimeout(resetTimer.current);
        resetTimer.current = setTimeout(() => setCopied(false), 1500);
      } catch {
        // Clipboard blocked (insecure context / denied permission) — the full
        // ID is still on screen via the tooltip, so fail quietly.
      }
    },
    [id],
  );

  const shown = variant === 'full' ? id : shortenSupportId(id);

  return (
    <button
      type="button"
      onClick={handleCopy}
      title={`${label}: ${id} — click to copy`}
      aria-label={`Copy ${label} ${id}`}
      className={cn(
        'group/refid inline-flex max-w-full items-center gap-1 rounded border border-transparent',
        'px-1 py-0.5 font-mono text-[11px] leading-none text-muted-foreground',
        'hover:border-border hover:bg-muted hover:text-foreground transition-colors',
        className,
      )}
    >
      {showLabel && <span className="font-sans flex-shrink-0">{label}</span>}
      {/* The short form is already elided to a bounded width, so it must NOT be
          CSS-truncated on top — that would clip the tail off and undo the
          middle-ellipsis. Only the full variant may be clipped by its column. */}
      <span className={variant === 'full' ? 'truncate' : 'whitespace-nowrap'}>{shown}</span>
      {copied ? (
        <Check className="h-3 w-3 flex-shrink-0 text-green-600" aria-hidden="true" />
      ) : (
        <Copy
          className="h-3 w-3 flex-shrink-0 opacity-0 group-hover/refid:opacity-100 transition-opacity"
          aria-hidden="true"
        />
      )}
      <span className="sr-only">{copied ? 'Copied' : ''}</span>
    </button>
  );
}
